/*
 * RoundMatchesView — the "Team" toggle target from the broadcast
 * MatchScoring page.
 *
 * Route: /app/tournament-rounds/:roundId/matches
 *
 * Renders the single round using the "Team View · Classic Lower Third"
 * design from Travis's design handoff. Hero scoreboard (this-round +
 * event-total subtitle), stat strip, per-match rows, footer. The
 * heavy lifting lives in <BroadcastRoundSection>.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { api } from '@/lib/api';
import { AppShell } from '@/components/AppShell';
import { BroadcastRoundSection, type SectionMatch, type SectionTeam } from '@/components/BroadcastRoundSection';
import { useAuthStore } from '@/store/authStore';

type MatchHole = {
  id: string;
  holeNumber: number;
  winnerTeamId: string | null;
  isHalved: boolean;
};

type Participant = {
  eventPlayerId: string;
  sideNumber: number;
  displayName: string;
  userId: string | null;
};

type RawMatch = {
  id: string;
  matchNumber: number;
  status: string;
  teamAId: string | null;
  teamBId: string | null;
  teamAPointsAwarded: number;
  teamBPointsAwarded: number;
  winningTeamId: string | null;
  finalResultDisplay: string | null;
  finalResultType: string | null;
};

type EventPlayer = {
  id: string;
  displayName: string;
  teamId: string | null;
};

type Team = {
  id: string;
  name: string;
  primaryColor: string | null;
  captainEventPlayerId: string | null;
};

type EventBundle = {
  id: string;
  name: string;
  pointsNeeded: number | null;
  totalAvailablePoints: number | null;
  teams: Team[];
  players: EventPlayer[];
};

export default function RoundMatchesView() {
  const { roundId } = useParams<{ roundId: string }>();
  const navigate = useNavigate();
  const myUserId = useAuthStore((s) => s.userId);

  const [event, setEvent] = useState<EventBundle | null>(null);
  const [round, setRound] = useState<{ id: string; eventId: string; name: string; roundNumber: number; courseNameSnapshot: string | null; defaultTeeNameSnapshot: string | null; parTotal: number | null; sessionType: string | null; formatType: string } | null>(null);
  const [matches, setMatches] = useState<SectionMatch[]>([]);
  const [allMatchesAcrossEvent, setAllMatchesAcrossEvent] = useState<RawMatch[]>([]);
  const [totalRoundsInEvent, setTotalRoundsInEvent] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!roundId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await api.getTournamentRound(roundId) as any;
        if (cancelled) return;
        setRound({
          id: r.id,
          eventId: r.eventId,
          name: r.name,
          roundNumber: r.roundNumber,
          courseNameSnapshot: r.courseNameSnapshot,
          defaultTeeNameSnapshot: r.defaultTeeNameSnapshot,
          parTotal: null,
          sessionType: r.sessionType,
          formatType: r.formatType,
        });

        const ev = await api.getTournament(r.eventId) as EventBundle;
        if (cancelled) return;
        setEvent(ev);

        const allRounds = await api.getTournamentRounds(r.eventId) as any;
        const roundList = Array.isArray(allRounds) ? allRounds : (allRounds.rounds ?? []);
        if (cancelled) return;
        setTotalRoundsInEvent(roundList.length);

        // Collect every match across the event so we can sum event-wide
        // points without re-fetching anything heavy. Round list returned
        // by GET /tournaments/:id/rounds doesn't include matches; we have
        // to ask each round for them. Cheap on a 4-round event, fine on a
        // 12-round one.
        const allMatches: RawMatch[] = [];
        for (const rr of roundList) {
          if (rr.id === r.id) continue;
          const detail = await api.getTournamentRound(rr.id) as any;
          if (cancelled) return;
          for (const m of (detail.matches ?? []) as RawMatch[]) {
            allMatches.push(m);
          }
        }
        for (const m of (r.matches ?? []) as RawMatch[]) {
          allMatches.push(m);
        }
        setAllMatchesAcrossEvent(allMatches);

        // For each match in THIS round, pull per-hole status + the
        // participant displayName mappings. Per-match catch keeps one
        // flaky request from killing the page, but if EVERY call fails
        // we surface the error instead of silently rendering TBD rows —
        // that trap masked a 403 permissions bug once already.
        let scoringFailures = 0;
        const scorings = await Promise.all(
          (r.matches ?? []).map((m: RawMatch) =>
            api.getMatchScoring(m.id)
              .then((s: any) => ({
                id: m.id,
                matchHoles: (s.matchHoles ?? []) as MatchHole[],
                participants: ((s.participants ?? []) as any[]).map((p) => ({
                  eventPlayerId: p.eventPlayerId,
                  sideNumber: p.sideNumber,
                  displayName: p.displayName,
                  userId: p.userId,
                } as Participant)),
              }))
              .catch(() => {
                scoringFailures++;
                return { id: m.id, matchHoles: [] as MatchHole[], participants: [] as Participant[] };
              }),
          ),
        );
        if (cancelled) return;
        if ((r.matches ?? []).length > 0 && scoringFailures === (r.matches ?? []).length) {
          throw new Error('Could not load match details. Pull to refresh or try again in a moment.');
        }
        const scoringMap = new Map(scorings.map((s) => [s.id, s]));

        const enriched: SectionMatch[] = (r.matches ?? []).map((m: RawMatch) => {
          const s = scoringMap.get(m.id);
          return {
            id: m.id,
            matchNumber: m.matchNumber,
            status: m.status,
            teamAId: m.teamAId,
            teamBId: m.teamBId,
            teamAPointsAwarded: m.teamAPointsAwarded ?? 0,
            teamBPointsAwarded: m.teamBPointsAwarded ?? 0,
            winningTeamId: m.winningTeamId,
            finalResultDisplay: m.finalResultDisplay,
            finalResultType: m.finalResultType,
            participants: s?.participants ?? [],
            matchHoles: s?.matchHoles ?? [],
          };
        });
        setMatches(enriched);
        setError(null);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'Failed to load matches');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [roundId]);

  // Map team_id → captain first name. Captain lookups go through the
  // event_players list since that's where displayName lives.
  const captainsByTeam = useMemo(() => {
    const m = new Map<string, string>();
    if (!event) return m;
    for (const t of event.teams) {
      if (!t.captainEventPlayerId) continue;
      const ep = event.players.find((p) => p.id === t.captainEventPlayerId);
      if (ep) m.set(t.id, firstName(ep.displayName));
    }
    return m;
  }, [event]);

  const teamA: SectionTeam | null = useMemo(() => {
    if (!event || !round) return null;
    const t = event.teams[0];
    if (!t) return null;
    return { id: t.id, name: t.name, primaryColor: t.primaryColor, captainFirstName: captainsByTeam.get(t.id) ?? null };
  }, [event, round, captainsByTeam]);

  const teamB: SectionTeam | null = useMemo(() => {
    if (!event || !round) return null;
    const t = event.teams[1];
    if (!t) return null;
    return { id: t.id, name: t.name, primaryColor: t.primaryColor, captainFirstName: captainsByTeam.get(t.id) ?? null };
  }, [event, round, captainsByTeam]);

  const eventTotals = useMemo(() => {
    let a = 0, b = 0;
    for (const m of allMatchesAcrossEvent) {
      a += m.teamAPointsAwarded ?? 0;
      b += m.teamBPointsAwarded ?? 0;
    }
    return { teamA: a, teamB: b };
  }, [allMatchesAcrossEvent]);

  const pointsToClinch = useMemo(() => {
    if (!event) return null;
    if (event.pointsNeeded != null) return Number(event.pointsNeeded);
    if (event.totalAvailablePoints != null) return Number(event.totalAvailablePoints) / 2 + 0.5;
    return null;
  }, [event]);

  if (loading) {
    return (
      <AppShell back={{ to: '/app/home', label: 'Home' }} fullBleed>
        <div className="t-loading"><div className="spinner" /></div>
      </AppShell>
    );
  }
  if (error || !round || !event) {
    return (
      <AppShell back={{ to: '/app/home', label: 'Home' }} fullBleed>
        <div className="t-empty">
          <div className="t-empty-title">Round not available</div>
          <div className="t-empty-hint">{error ?? 'Could not load the round.'}</div>
        </div>
      </AppShell>
    );
  }

  const eventBack = { to: `/app/tournaments/${round.eventId}`, label: 'Event' };
  const myMatch = matches.find((m) => (m.participants ?? []).some((p) => p.userId === myUserId));

  return (
    <AppShell back={eventBack} fullBleed>
      <div style={{ background: 'var(--bcast-bg)', minHeight: '100%', color: 'var(--bcast-ink-1)' }}>
        {/* Top row with course title + Team / My Match toggle */}
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 14, padding: '14px 28px',
            borderBottom: '1px solid var(--bcast-line)',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--bcast-ink-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {round.courseNameSnapshot ?? round.name}
            </div>
            <div style={{ fontSize: 12, color: 'var(--bcast-ink-3)', fontWeight: 600, marginTop: 2 }}>
              {round.defaultTeeNameSnapshot ? `${round.defaultTeeNameSnapshot} tees` : ''}
            </div>
          </div>
          <div className="bcast-toggle" role="tablist" aria-label="Match view">
            <button
              type="button"
              className="bcast-toggle-btn is-active"
              role="tab"
              aria-selected={true}
            >
              Team
            </button>
            <button
              type="button"
              className="bcast-toggle-btn"
              onClick={() => myMatch && navigate(`/app/tournament-matches/${myMatch.id}/score`)}
              disabled={!myMatch}
              role="tab"
              aria-selected={false}
              title={myMatch ? 'Jump to your match' : "You're not in a match in this round"}
            >
              My Match
            </button>
          </div>
        </div>

        <BroadcastRoundSection
          round={{
            id: round.id,
            name: round.name,
            roundNumber: round.roundNumber,
            totalRoundsInEvent: totalRoundsInEvent,
            courseNameSnapshot: round.courseNameSnapshot,
            defaultTeeNameSnapshot: round.defaultTeeNameSnapshot,
            parTotal: round.parTotal,
            sessionType: round.sessionType,
            formatType: round.formatType,
          }}
          teamA={teamA}
          teamB={teamB}
          matches={matches}
          eventTotals={eventTotals}
          pointsToClinch={pointsToClinch}
          myUserId={myUserId}
        />
      </div>
    </AppShell>
  );
}

function firstName(s: string): string {
  return (s ?? '').trim().split(/\s+/)[0] || s;
}
